/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { UnsubscribeFn } from '@oneaudi/dealer-context-service';
import type { EnumerableFootnote } from '@oneaudi/footnote-reference-service';
import React, { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import type { IHeaderAction } from '../../actions/header-actions';
import { createSetActiveItemAction } from '../../actions/header-actions';
import { DealerContext, OidcUserContext } from '../../context';
import { getDealerData } from '../../graphql/get-dealer';
import type {
  AudiHeaderDataManagerProps,
  ConnectedData,
  Dealer,
} from '../../interfaces/header-components.interfaces';
import type HeaderResponse from '../../interfaces/header-response.interfaces';
import type { HeaderState } from '../../reducers/header-reducer';
import { headerReducer } from '../../reducers/header-reducer';
import { createUseFootnoteReferenceServiceTextParserHook } from '../../services/hooks';
import { fetchHeaderConfig } from '../../services/navigation-service';
import { cleanNavigationEntries } from '../../utils/clean-navigation-entries';
import {
  AUTH_OIDC_USER_INFO,
  DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE,
  DEALER_CONTEXT_UNSUBSCRIBE,
  KEY_DEALER_DATA,
} from '../../utils/globals';
import { isEmpty } from '../../utils/is-empty';
import type { NormalizeContentConfig } from '../../utils/normalize-falcon-data';
import { normalizeContentHeadless } from '../../utils/normalize-falcon-data';
import { replacePartnerId, renderPartnerId } from '../../utils/replace-partner';
import AudiHeader from './AudiHeader';
import { ServiceContext } from '../../context/ServiceContext';
import { ConfigContext } from '../../context/ConfigContext';
import { matchCountryUrlOrigin } from '../../utils/match-url-pattern';
import { useAuthFetch } from '../../hooks/use-auth-fetch';
import type { OidcUser } from '../../types/oidc-user-info';
import { removeSessionData } from '../../utils/session-utils';
import { getDealerSearchHashParam, removePartnerID } from '../../utils/append-partner-id';
import { isDealerDomain } from '../../utils/is-dealer-domain';
import { removeDealerTrackingData } from '../../utils/tracking';
import { useDealerNotification } from '../../hooks/use-dealer-notification';
import useGeolocation from '../../hooks/use-geolocation';

const AudiHeaderDataManager: React.FC<AudiHeaderDataManagerProps> = ({
  ssrHeaderData,
  headerConfigDataUrl,
  enablePartnerIdReplacement,
  contentHeadless,
}) => {
  const { dealerContextService, footnoteReferenceService, headerStateService, trackingService } =
    useContext(ServiceContext);
  const {
    renderDealerContext,
    useOneLayer,
    useUserMenu,
    disableMyAudiMenu,
    tierOneUrl,
    OneShopEnabled,
    dealerSearch,
    dealerSearchGeolocationEnabled,
  } = useContext(ConfigContext);

  const isOneCms = contentHeadless?.__type === 'aem-headless';
  const [footnotes, setFootnotes] = useState([]);
  const [partnerId, setPartnerId] = useState(undefined);
  const [partnerIdQueryString, setPartnerIdQueryString] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [headerConfig, setHeaderConfig] = useState<HeaderResponse | undefined>(ssrHeaderData);
  const [displayUserMenu, setDisplayUserMenu] = useState<boolean>(useUserMenu || false);
  const [dealerData, setDealerData] = useState<Dealer | undefined>(undefined);
  const [dealerConnectedNotification, setDealerConnectedNotification] =
    useState<ConnectedData | null>(null);

  const isTierOneDomain = isDealerDomain(tierOneUrl);

  const wrapperElement = useRef<HTMLDivElement>(null);
  const navigationWrapperElement = useRef<HTMLDivElement>(null);
  const actionWrapperElement = useRef<HTMLDivElement>(null);
  const logoWrapperElement = useRef<HTMLDivElement>(null);
  const dealerId = dealerContextService?.getSelectedDealer();
  // get OIDC user info if user is authenticated
  const {
    data: oidcUser,
    loading,
    error,
  }: {
    data: OidcUser | null;
    loading: boolean;
    error: string | null;
  } = useAuthFetch(
    AUTH_OIDC_USER_INFO.url,
    {
      method: 'GET',
      resourceHostId: AUTH_OIDC_USER_INFO.resourceHostId,
    },
    AUTH_OIDC_USER_INFO.sessionKey,
  );

  const useFootnoteReferenceServiceTextParserHook = createUseFootnoteReferenceServiceTextParserHook(
    {
      footnotes,
    },
  );

  const autoConnectedUnsubscribed =
    typeof window !== 'undefined' &&
    Boolean(sessionStorage.getItem(DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE));

  const { geoError } = useGeolocation(
    dealerSearch,
    dealerSearchGeolocationEnabled,
    autoConnectedUnsubscribed,
  );

  const [headerState, headerDispatch] = useReducer<React.Reducer<HeaderState, IHeaderAction>>(
    headerReducer,
    {
      activeItem: {
        anchor: null,
        index: -1,
        showSearch: false,
        showDealer: false,
        showDealerSearch: false,
        showLoginFlyout: false,
        showWistListFlyout: false,
        openDealerDetails: false,
      },
    },
  );
  // logic to disable user menu on tier 3 markets
  useEffect(() => {
    if (disableMyAudiMenu) {
      if (useUserMenu && !matchCountryUrlOrigin(window.location.origin, tierOneUrl || '')) {
        setDisplayUserMenu(false);
      }
    }
  }, [disableMyAudiMenu, tierOneUrl, useUserMenu]);

  useEffect(() => {
    let unsubscribeDealerContext: UnsubscribeFn;

    if (renderDealerContext) {
      unsubscribeDealerContext = dealerContextService.subscribe((dealerContext) => {
        if (typeof window !== 'undefined' && dealerContext) {
          // if dealerSearchGeolocationEnabled is enabled, First check if KEY_DEALER_DATA is set to unsubscribe
          if (dealerSearchGeolocationEnabled) {
            const dealerStore = sessionStorage.getItem(KEY_DEALER_DATA);
            // if true the user has volunteered to be unsubscribed from autoconnected dealer
            if (dealerStore === DEALER_CONTEXT_UNSUBSCRIBE) {
              removeSessionData(KEY_DEALER_DATA); // remove storage KEY_DEALER_DATA
              // Dispatch custom event for dealer data deletion
              window.dispatchEvent(new CustomEvent('dealerDataUpdated', { detail: null }));
            }
          }

          // if true the user has volunteered to be unsubscribed from autoconnected dealer
          const autoConnectUnsubscribe = Boolean(
            sessionStorage.getItem(DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE),
          );

          const dealerIdFromContext = `${dealerContext?.market}${dealerContext?.brand}${dealerContext?.id}`;
          const thePartnerId = renderPartnerId(dealerContext?.id, dealerContext?.market, isOneCms);
          setPartnerIdQueryString(thePartnerId);
          // re-check KEY_DEALER_DATA storage
          const store = sessionStorage.getItem(KEY_DEALER_DATA);

          // in the event a user decides to reconnect to a dealership via "select a dealer", check for params created by the dealer search FA
          let hasConsumerLocationsQueryString: boolean = false;
          if (autoConnectUnsubscribe && store && store !== DEALER_CONTEXT_UNSUBSCRIBE) {
            hasConsumerLocationsQueryString = getDealerSearchHashParam('consumerLocations'); // 'consumerLocations' the param created by the dealer search FA
          }

          if (store !== DEALER_CONTEXT_UNSUBSCRIBE) {
            const storageData: Dealer = JSON.parse(store || '{}');
            if (
              storageData?.dealerId?.toString() !== dealerIdFromContext &&
              !hasConsumerLocationsQueryString
            ) {
              getDealerData(process.env.GRAPHQL_DEALER_ENDPOINT, dealerIdFromContext)
                .then((data) => {
                  if (data) {
                    const { dealerById } = data;
                    setDealerData(dealerById);
                    sessionStorage.setItem(KEY_DEALER_DATA, JSON.stringify(dealerById));
                    // @ts-expect-error
                    setDealerConnectedNotification((prev) => ({
                      ...prev,
                      isConnected: true,
                    }));
                    // Dispatch custom event for dealer data update
                    window.dispatchEvent(
                      new CustomEvent('dealerDataUpdated', { detail: dealerById }),
                    );
                  }
                })
                .catch(() => {
                  if (!isEmpty(storageData)) {
                    setDealerData(storageData);
                  }

                  console.error(`[fa-nemo-header] dealer id ${dealerIdFromContext} not found`);
                });
            } else if (!isEmpty(storageData)) {
              setDealerData(storageData);
            }
          }
        } else setPartnerIdQueryString('');
      });
    }
    return (): void => {
      if (renderDealerContext) {
        unsubscribeDealerContext();
      }
    };
  }, [dealerContextService, renderDealerContext]);

  // Create dealer notification or dealer notification suggestion
  useDealerNotification({
    isTierOneDomain,
    dealerData,
    headerDispatch,
    setDealerConnectedNotification,
  });

  // if no dealer context session saved anymore or removed from another FA, set KEY_DEALER_DATA to DEALER_CONTEXT_UNSUBSCRIBE
  useEffect(() => {
    if (!dealerId) {
      const storageData: Dealer | string | null = sessionStorage.getItem(KEY_DEALER_DATA);
      if (storageData && storageData !== DEALER_CONTEXT_UNSUBSCRIBE) {
        sessionStorage.removeItem(KEY_DEALER_DATA); // remove storage KEY_DEALER_DATA
        dealerContextService.setSelectedDealer(undefined);
        setDealerData(undefined);
        // Dispatch custom event for dealer data deletion
        window.dispatchEvent(new CustomEvent('dealerDataUpdated', { detail: null }));
      }
    }
  }, [dealerId]);

  useEffect(() => {
    if (window && window.location.href.indexOf('debug=true') !== -1) {
      headerStateService.setDebugMode(true);
    }
    if (footnoteReferenceService) {
      footnoteReferenceService.registerCallback((_footnotes: EnumerableFootnote[]) => {
        // @ts-ignore
        setFootnotes(_footnotes);
      });
    }

    return (): void => {
      if (window) {
        if (window.location.href.indexOf('debug=true') !== -1) {
          headerStateService.setDebugMode(true);
        } else {
          headerStateService.setDebugMode(false);
        }
      }
      if (footnoteReferenceService) {
        footnoteReferenceService.removeFootnoteReferences();
      }
    };
  }, []);

  useEffect(() => {
    if (navigationWrapperElement?.current) {
      setIsLoading(false);
    }
  }, [navigationWrapperElement]);

  useEffect(() => {
    if (!isOneCms) {
      // always load clientside again, to be sure, not to work with a cached version from SSR
      fetchHeaderConfig(headerConfigDataUrl)
        .then((response) => {
          setHeaderConfig(
            cleanNavigationEntries(
              enablePartnerIdReplacement ? replacePartnerId(response, partnerId) : response,
            ),
          );
        })

        .catch((headerConfigError) => {
          console.error(headerConfigError);
        });
    } else {
      const normalizeConfig: NormalizeContentConfig = { tierOneUrl, OneShopEnabled };
      setHeaderConfig(
        enablePartnerIdReplacement
          ? replacePartnerId(
              normalizeContentHeadless(contentHeadless, normalizeConfig, partnerIdQueryString),
              partnerId,
            )
          : normalizeContentHeadless(contentHeadless, normalizeConfig, partnerIdQueryString),
      );
    }
    setIsLoading(false);
  }, [headerConfigDataUrl, ssrHeaderData, partnerId, partnerIdQueryString]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !enablePartnerIdReplacement ||
      !(window.microkernel && window.microkernel.stateRegistry)
    ) {
      return (): void => {
        // Do nothing
      };
    }

    const partnerCallback = (state: React.SetStateAction<undefined>): void => {
      setPartnerId(state);
    };

    window.microkernel.stateRegistry.subscribeToStore('dbadDealerStore', partnerCallback);

    return (): void => {
      window.microkernel.stateRegistry.unsubscribeFromStore('dbadDealerStore', partnerCallback);
    };
  }, [enablePartnerIdReplacement, setPartnerId]);

  const removeDealer = useCallback(
    (label?: string): void => {
      setDealerData(undefined);
      dealerContextService.setSelectedDealer(undefined); // remove dealer-context from session storage
      setDealerConnectedNotification(null);
      // only set KEY_DEALER_DATA to DEALER_CONTEXT_UNSUBSCRIBE if auto connect dealer search geolocation is enabled
      if (dealerSearchGeolocationEnabled) {
        removePartnerID();
        sessionStorage?.setItem(KEY_DEALER_DATA, DEALER_CONTEXT_UNSUBSCRIBE);
        sessionStorage?.setItem(DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE, 'true');
      }

      // No dealerSearch config enabled keep original DEALER_CONTEXT_UNSUBSCRIBE functionality
      if (!dealerSearch) {
        sessionStorage?.setItem(KEY_DEALER_DATA, DEALER_CONTEXT_UNSUBSCRIBE);
      }

      headerDispatch(
        createSetActiveItemAction({
          anchor: null,
          headerStateService,
          index: -1,
          showSearch: false,
          showDealer: false,
          showDealerSearch: false,
          showLoginFlyout: false,
          showWistListFlyout: false,
          openDealerDetails: false,
        }),
      );

      trackingService?.track(removeDealerTrackingData(label || ''));

      window.dispatchEvent(new CustomEvent('dealerDataUpdated', { detail: null }));
    },
    [setDealerData],
  );

  // Listen for custom event from other Feature Apps to open dealer details
  useEffect(() => {
    const handleOpenDealerDetails = (): void => {
      // If there's dealer data, open dealer details; otherwise open dealer search
      const hasDealerData = !!dealerData;

      let isMobileView = false;
      headerStateService.registerCallback((headerStateRemote) => {
        isMobileView = headerStateRemote.isMobileView;
      });

      // Open mobile menu if on mobile
      if (isMobileView) {
        window.dispatchEvent(new CustomEvent('openMobileMenu'));
      }

      headerDispatch(
        createSetActiveItemAction({
          anchor: null,
          headerStateService,
          index: -1,
          showSearch: false,
          showDealer: hasDealerData,
          showDealerSearch: !hasDealerData,
          showLoginFlyout: false,
          showWistListFlyout: false,
          openDealerDetails: !headerState.activeItem.openDealerDetails,
        }),
      );
    };

    window.addEventListener('openDealerDetails', handleOpenDealerDetails);

    return (): void => {
      window.removeEventListener('openDealerDetails', handleOpenDealerDetails);
    };
  }, [headerDispatch, headerStateService, dealerData, headerState]);

  return (
    <OidcUserContext.Provider value={{ oidcUser, loading, error }}>
      <DealerContext.Provider
        value={{
          renderDealerContext,
          dealerData,
          dealerConnectedNotification,
          removeDealer,
          geoError,
          autoConnectedUnsubscribed,
          setDealerConnectedNotification,
        }}
      >
        <AudiHeader
          wrapperElement={wrapperElement}
          navigationWrapperElement={navigationWrapperElement}
          actionWrapperElement={actionWrapperElement}
          logoWrapperElement={logoWrapperElement}
          headerConfig={headerConfig}
          headerDispatch={headerDispatch}
          headerState={headerState}
          isLoading={isLoading}
          useFootnoteReferenceServiceTextParserHook={useFootnoteReferenceServiceTextParserHook}
          useOneLayer={useOneLayer}
          useUserMenu={displayUserMenu}
          isOneCms={isOneCms}
          data-tracking-exclude
        />
      </DealerContext.Provider>
    </OidcUserContext.Provider>
  );
};

export default AudiHeaderDataManager;
